import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {
    generateTopicContent,
    updateTopicContent,
    LLMContentRequest,
    CoreTopicDTO
} from '../../services/adminCourseService';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Stack,
    Tabs,
    Tab,
    Divider,
    Alert,
    Snackbar
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    AutoAwesome as AutoIcon,
    Preview as PreviewIcon,
    Code as CodeIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';

interface TopicContentManagerProps {
    subjectId: number;
    topic: CoreTopicDTO;
    onContentUpdate: (updatedTopic: CoreTopicDTO) => void;
}

const TopicContentManager: React.FC<TopicContentManagerProps> = ({
    subjectId,
    topic,
    onContentUpdate
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState(topic.content || '');
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showAIModal, setShowAIModal] = useState(false);

    // Error States
    const [saveError, setSaveError] = useState<string | null>(null);
    const [genError, setGenError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // AI Inputs
    const [aiContext, setAiContext] = useState('');
    const [aiType, setAiType] = useState('Lesson');
    const [aiDifficulty, setAiDifficulty] = useState('Intermediate');
    const [previewTab, setPreviewTab] = useState(0);

    useEffect(() => {
        setContent((topic as any).content || '');
    }, [topic]);

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError(null);
        try {
            await updateTopicContent(topic.id, content);

            const updatedTopic = { ...topic, content: content };
            onContentUpdate(updatedTopic);

            setIsEditing(false);
            setSaveSuccess(true);
        } catch (error: any) {
            console.error('Failed to save content:', error);
            setSaveError(error.response?.data?.message || 'Failed to save content. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleGenerateAI = async () => {
        setIsGenerating(true);
        setGenError(null);
        try {
            const request: LLMContentRequest = {
                topicTitle: topic.title,
                context: aiContext,
                contentType: aiType,
                difficultyLevel: aiDifficulty
            };

            const generatedContent = await generateTopicContent(subjectId, topic.id, request);
            setContent(generatedContent);
            setShowAIModal(false);
        } catch (error: any) {
            console.error('Failed to generate content:', error);
            setGenError(error.response?.data?.message || 'Failed to generate content from AI. Please check the system logs.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handlePreviewTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setPreviewTab(newValue);
    };

    if (!isEditing) {
        return (
            <Paper elevation={0} variant="outlined" sx={{ p: 4, minHeight: 400 }}>
                {saveSuccess && (
                    <Snackbar open={saveSuccess} autoHideDuration={6000} onClose={() => setSaveSuccess(false)}>
                        <Alert severity="success" sx={{ width: '100%' }}>Content saved successfully!</Alert>
                    </Snackbar>
                )}

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" color="text.primary" fontWeight="medium">
                        Topic Content
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => setIsEditing(true)}
                        sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                    >
                        Edit Content
                    </Button>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {content ? (

                    <Box
                        sx={{
                            '& h1': { fontSize: '1.75rem', fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' },
                            '& h2': { fontSize: '1.4rem', fontWeight: 600, mb: 1.5, mt: 2.5, borderBottom: '1px solid', borderColor: 'divider', pb: 1 },
                            '& h3': { fontSize: '1.15rem', fontWeight: 600, mb: 1, mt: 2 },
                            '& p': { mb: 1.5, lineHeight: 1.7 },
                            '& ul, & ol': { pl: 3, mb: 2 },
                            '& li': { mb: 0.5 },
                            '& blockquote': {
                                borderLeft: '4px solid',
                                borderColor: 'primary.main',
                                bgcolor: 'action.hover',
                                m: 0,
                                p: 2,
                                mb: 2,
                                borderRadius: 1,
                                fontStyle: 'italic'
                            },
                            '& table': {
                                borderCollapse: 'collapse',
                                width: '100%',
                                mb: 3,
                                mt: 2
                            },
                            '& th': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                p: 1.5,
                                textAlign: 'left',
                                fontWeight: 600
                            },
                            '& td': {
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 1.5
                            },
                            '& tr:nth-of-type(even)': {
                                bgcolor: 'action.hover'
                            },
                            '& pre': {
                                bgcolor: 'grey.900',
                                color: 'grey.100',
                                p: 2,
                                borderRadius: 1,
                                overflow: 'auto',
                                mb: 2,
                                fontFamily: 'monospace'
                            },
                            '& code': {
                                fontFamily: 'monospace',
                                fontWeight: 'bold',
                                bgcolor: 'action.hover',
                                p: 0.5,
                                borderRadius: 1
                            }
                        }}
                    >
                        <ReactMarkdown
                            remarkPlugins={[remarkMath, remarkGfm]}
                            rehypePlugins={[rehypeKatex]}
                            components={{
                                blockquote: ({ node, ...props }) => {
                                    return <Box component="blockquote" {...props} />;
                                },
                                table: ({ node, ...props }) => (
                                    <Box sx={{ overflowX: 'auto', mb: 2 }}>
                                        <table {...props} />
                                    </Box>
                                )
                            }}
                        >
                            {content}
                        </ReactMarkdown>
                    </Box>
                ) : (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={8}
                        sx={{ backgroundColor: '#fafafa', borderRadius: 2, border: '1px dashed #bdbdbd' }}
                    >
                        <Typography color="text.secondary" gutterBottom>
                            No content available for this topic yet.
                        </Typography>
                        <Button
                            onClick={() => setIsEditing(true)}
                            color="primary"
                        >
                            Start Writing
                        </Button>
                    </Box>
                )}

            </Paper >
        );
    }

    // Edit Mode
    return (
        <Paper elevation={0} variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Toolbar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Tabs
                    value={previewTab}
                    onChange={handlePreviewTabChange}
                    aria-label="editor tabs"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab icon={<CodeIcon fontSize="small" />} iconPosition="start" label="Write" />
                    <Tab icon={<PreviewIcon fontSize="small" />} iconPosition="start" label="Preview" />
                </Tabs>

                <Stack direction="row" spacing={2}>
                    <Button
                        startIcon={<AutoIcon />}
                        variant="outlined"
                        color="secondary"
                        onClick={() => setShowAIModal(true)}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        AI Assistant
                    </Button>
                    <Button
                        onClick={() => setIsEditing(false)}
                        color="inherit"
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        variant="contained"
                        disabled={isSaving}
                        sx={{ borderRadius: 2, textTransform: 'none', minWidth: 100 }}
                    >
                        Save
                    </Button>
                </Stack>
            </Box>

            {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

            {/* Editor Area */}
            <Box flexGrow={1} sx={{ position: 'relative', minHeight: 500 }}>
                {previewTab === 0 ? (
                    <TextField
                        fullWidth
                        multiline
                        minRows={20}
                        maxRows={30} // Limit max height to keep inside viewport
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="# Introduction\n\nStart writing your content here..."
                        variant="outlined"
                        InputProps={{
                            sx: { fontFamily: 'monospace', fontSize: '0.9rem', height: '100%', alignItems: 'flex-start' }
                        }}
                        sx={{
                            height: '100%',
                            '& .MuiInputBase-root': { height: '100%' },
                            '& .MuiInputBase-input': { height: '100%', overflow: 'auto' }
                        }}
                    />
                ) : (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 3,
                            height: '100%',
                            overflow: 'auto',
                            bgcolor: '#fafafa'
                        }}
                    >
                        <Box
                            sx={{
                                '& h1': { fontSize: '1.75rem', fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' },
                                '& h2': { fontSize: '1.4rem', fontWeight: 600, mb: 1.5, mt: 2.5, borderBottom: '1px solid', borderColor: 'divider', pb: 1 },
                                '& h3': { fontSize: '1.15rem', fontWeight: 600, mb: 1, mt: 2 },
                                '& p': { mb: 1.5, lineHeight: 1.7 },
                                '& ul, & ol': { pl: 3, mb: 2 },
                                '& li': { mb: 0.5 },
                                '& blockquote': {
                                    borderLeft: '4px solid',
                                    borderColor: 'primary.main',
                                    bgcolor: 'action.hover',
                                    m: 0,
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 1,
                                    fontStyle: 'italic'
                                },
                                '& table': {
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    mb: 3,
                                    mt: 2
                                },
                                '& th': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    p: 1.5,
                                    textAlign: 'left',
                                    fontWeight: 600
                                },
                                '& td': {
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    p: 1.5
                                },
                                '& tr:nth-of-type(even)': {
                                    bgcolor: 'action.hover'
                                },
                                '& pre': {
                                    bgcolor: 'grey.900',
                                    color: 'grey.100',
                                    p: 2,
                                    borderRadius: 1,
                                    overflow: 'auto',
                                    mb: 2,
                                    fontFamily: 'monospace'
                                },
                                '& code': {
                                    fontFamily: 'monospace',
                                    fontWeight: 'bold',
                                    bgcolor: 'action.hover',
                                    p: 0.5,
                                    borderRadius: 1
                                }
                            }}
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkMath, remarkGfm]}
                                rehypePlugins={[rehypeKatex]}
                                components={{
                                    blockquote: ({ node, ...props }) => {
                                        return <Box component="blockquote" {...props} />;
                                    },
                                    table: ({ node, ...props }) => (
                                        <Box sx={{ overflowX: 'auto', mb: 2 }}>
                                            <table {...props} />
                                        </Box>
                                    )
                                }}
                            >
                                {content || '*No content to preview*'}
                            </ReactMarkdown>
                        </Box>
                    </Paper>
                )}
            </Box>

            {/* AI Modal */}
            <Dialog open={showAIModal} onClose={() => setShowAIModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoIcon color="secondary" />
                    Generate Content with AI
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {genError && <Alert severity="error">{genError}</Alert>}

                        <Alert severity="info">
                            This will generate a structured lesson including headers, examples, and summaries.
                            Existing content will be overwritten.
                        </Alert>

                        <FormControl fullWidth>
                            <InputLabel>Content Type</InputLabel>
                            <Select
                                value={aiType}
                                label="Content Type"
                                onChange={(e) => setAiType(e.target.value)}
                            >
                                <MenuItem value="Lesson">Comprehensive Lesson</MenuItem>
                                <MenuItem value="Summary">Quick Summary</MenuItem>
                                <MenuItem value="Study Guide">Study Guide</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                                value={aiDifficulty}
                                label="Difficulty"
                                onChange={(e) => setAiDifficulty(e.target.value)}
                            >
                                <MenuItem value="Beginner">Beginner (Easy)</MenuItem>
                                <MenuItem value="Intermediate">Intermediate (Standard)</MenuItem>
                                <MenuItem value="Advanced">Advanced (Complex)</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="Specific Instructions"
                            multiline
                            rows={3}
                            value={aiContext}
                            onChange={(e) => setAiContext(e.target.value)}
                            placeholder="e.g., Focus on real-world examples, include 3 specific scenarios..."
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setShowAIModal(false)} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleGenerateAI}
                        variant="contained"
                        color="secondary"
                        disabled={isGenerating}
                        startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoIcon />}
                    >
                        {isGenerating ? 'Generating...' : 'Generate Content'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default TopicContentManager;
